<template>
  <!--
    A toast appears without the user asking for it and removes itself, so it
    has to be announced. `alert` is assertive and interrupts, which is right
    for a failure; everything else is `status`, which waits for a pause.
  -->
  <div
    class="toast"
    :role="liveRole"
    :aria-live="liveRole === 'alert' ? 'assertive' : 'polite'"
  >
    <div v-if="type">
      <div class="toast__icon" :class="`toast__icon--${type}`">
        <i v-if="loading" class="toast__loading" aria-hidden="true"></i>
        <i v-else-if="icon" :class="icon" aria-hidden="true"></i>
      </div>
    </div>

    <div class="toast__content">
      <div v-if="hasTitleSlot" class="toast__title">
        <slot name="title" />
      </div>
      <p v-if="hasContentSlot" class="toast__message"><slot /></p>

      <div v-if="hasActionsSlot" class="toast__actions">
        <slot name="actions" />
      </div>
    </div>

    <button
      v-if="closeButton"
      type="button"
      class="toast__close"
      :aria-label="$t('action.close')"
      @click="$emit('close')"
    >
      <i class="iconoir-cancel" aria-hidden="true"></i>
    </button>
  </div>
</template>

<script>
import { TOAST_AUTO_DISMISS_MS } from '@baserow/modules/core/utils/toastConstants'

export default {
  name: 'Toast',
  props: {
    /**
     * The type of toast to display
     */
    type: {
      type: String,
      default: 'info-primary',
      validator: function (value) {
        return [
          'info-neutral',
          'info-primary',
          'warning',
          'error',
          'success',
        ].includes(value)
      },
    },
    /**
     * The icon to display in the toast.
     */
    icon: {
      type: String,
      default: null,
    },
    /**
     * Whether to display a loading spinner or not.
     */
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    /**
     * Whether to display a close button or not.
     */
    closeButton: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  computed: {
    hasTitleSlot() {
      return !!this.$slots.title
    },
    hasActionsSlot() {
      return !!this.$slots.actions
    },
    hasContentSlot() {
      return !!this.$slots.default
    },
    liveRole() {
      return this.type === 'error' ? 'alert' : 'status'
    },
  },
  mounted() {
    // The handle is kept so the timer can be cancelled on unmount. Previously
    // the timeout was fired and forgotten, so a toast closed by its own button
    // still had a pending callback that emitted `close` again after the
    // component was gone.
    this.autoCloseTimeout = setTimeout(() => {
      this.autoCloseTimeout = null
      this.close()
    }, TOAST_AUTO_DISMISS_MS)
  },
  beforeUnmount() {
    if (this.autoCloseTimeout) {
      clearTimeout(this.autoCloseTimeout)
      this.autoCloseTimeout = null
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
  },
}
</script>
